import React, { useContext, useMemo } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { ViewEntityPage } from 'components/entityViewer/ViewEntityPage';
import { FCTransferEntityHeader } from 'components/warehouse/components/fcTransfers/entityView/FCTransferEntityHeader';
import { FCTransferInfoCard } from 'components/warehouse/components/fcTransfers/entityView/FCTransferInfoCard';
import { FCTransferItemsTableCard } from 'components/warehouse/components/fcTransfers/entityView/FCTransferItemsTableCard';
import { FC_TRANSFER_DIRECTION } from 'components/warehouse/components/fcTransfers/FCTransferFilterConfig';
import { useFCTransferById } from 'components/warehouse/components/fcTransfers/queries/FCTransferQueries';
import { UserContext } from 'contexts/UserContext';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';

/**
 * @param {Record<string, unknown> | null | undefined} transfer
 * @param {string | number | null | undefined} currentFacilityId
 * @returns {'OUTBOUND' | 'INBOUND'}
 */
function resolveDirectionForFacility(transfer, currentFacilityId) {
    if (transfer == null || currentFacilityId === undefined || currentFacilityId === null || currentFacilityId === '') {
        return FC_TRANSFER_DIRECTION.OUTBOUND;
    }
    const fid = String(currentFacilityId);
    const src = transfer.sourceFacilityId ?? transfer.source_facility_id;
    const dest = transfer.destinationFacilityId ?? transfer.destination_facility_id;
    if (src !== undefined && src !== null && String(src) === fid) {
        return FC_TRANSFER_DIRECTION.OUTBOUND;
    }
    if (dest !== undefined && dest !== null && String(dest) === fid) {
        return FC_TRANSFER_DIRECTION.INBOUND;
    }
    return FC_TRANSFER_DIRECTION.OUTBOUND;
}

/**
 * FC Transfer entity view
 */
export function ViewFCTransferEntity() {
    const { transferId } = useParams();
    const { user } = useContext(UserContext);
    const currentFacilityId = user?.facilityID;

    const {
        data: transfer,
        error,
        isError,
        isLoading,
    } = useFCTransferById({
        transferId,
        enabled: Boolean(transferId),
    });

    const direction = useMemo(
        () => resolveDirectionForFacility(transfer, currentFacilityId),
        [transfer, currentFacilityId],
    );

    const items = useMemo(() => {
        if (transfer && Array.isArray(transfer.items)) {
            return transfer.items;
        }
        return [];
    }, [transfer]);

    const backHref = '/warehouse/fc-transfers';

    if (isLoading) {
        return (
            <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 px-4">
                <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" aria-hidden />
                <p className="text-sm text-muted-foreground">Loading transfer…</p>
            </div>
        );
    }

    if (isError || !transfer) {
        return (
            <div className="flex flex-col gap-4 px-4 py-6">
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Could not load transfer</AlertTitle>
                    <AlertDescription>{error?.message ?? 'Transfer not found or unavailable.'}</AlertDescription>
                </Alert>
                <Button variant="outline" asChild>
                    <Link to={backHref}>Back to FC Transfers</Link>
                </Button>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen flex-col px-4 pb-8 pt-4">
            <ViewEntityPage
                header={
                    <FCTransferEntityHeader
                        backHref={backHref}
                        direction={direction}
                        transfer={transfer}
                        onEditTransfer={() => {}}
                    />
                }
                primaryInformationCard={<FCTransferInfoCard transfer={transfer} />}
                additionalInformationCards={<FCTransferItemsTableCard items={items} />}
            />
        </div>
    );
}

ViewFCTransferEntity.displayName = 'ViewFCTransferEntity';
